import AppUpdateWarning from "@/Components/UI/Modals/AppUpdateWarning";
import { AiDraftProvider } from "@/Contexts/AiDraftProvider";
import { ChatProvider } from "@/Contexts/ChatProvider";
import { ClientExercisesProvider } from "@/Contexts/ClientExercisesProvider";
import { ExercisePickerProvider } from "@/Contexts/ExercisePickerProvider";
import { ExercisesSeenProvider } from "@/Contexts/ExercisesSeenProvider";
import { FollowupTaskProvider } from "@/Contexts/FollowupTaskProvider";
import { GiftCoachingProvider } from "@/Contexts/GiftCoachingProvider";
import { PayUsModalsProvider } from "@/Contexts/PayUsModalsProvider";
import { RequiredProviders } from "@/Contexts/RequiredProviders";
import { TrainingsProvider } from "@/Contexts/TrainingsProvider";
import useIntercomMessenger from "@/utils/useIntercomMessenger";
import { PropsWithChildren } from "react";
import { BoardAccessProvider } from "./BoardAccessProvider";
import { CoachSidebarDataProvider } from "./CoachSidebarDataProvider";

/**
 * Global state providers do not refresh between Inertia page loads. This is
 * great if you want a component to persist between loads, but this means
 * Inertia props will not automatically refresh.
 *
 * Use this if you want to avoid unnecessary component remounts and data fetches
 * on page navigations
 *
 * Don't use this if you need your component to refresh on navigation, or you
 * don't understand the consequences of using this
 *
 * Everything shared with the marketing SPA lives in RequiredProviders. Only add
 * a provider here when a logged-out marketing page cannot use it.
 */
export function GlobalStateProviders({ children }: PropsWithChildren) {
    useIntercomMessenger();

    return (
        <RequiredProviders>
            <AppUpdateWarning>
                <PayUsModalsProvider>
                    <ChatProvider>
                        <CoachSidebarDataProvider>
                            <FollowupTaskProvider>
                                <ExercisesSeenProvider>
                                    <TrainingsProvider>
                                        <GiftCoachingProvider>
                                            <ClientExercisesProvider>
                                                <BoardAccessProvider>
                                                    <ExercisePickerProvider>
                                                        <AiDraftProvider>
                                                            {children}
                                                        </AiDraftProvider>
                                                    </ExercisePickerProvider>
                                                </BoardAccessProvider>
                                            </ClientExercisesProvider>
                                        </GiftCoachingProvider>
                                    </TrainingsProvider>
                                </ExercisesSeenProvider>
                            </FollowupTaskProvider>
                        </CoachSidebarDataProvider>
                    </ChatProvider>
                </PayUsModalsProvider>
            </AppUpdateWarning>
        </RequiredProviders>
    );
}
